import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import GridCanvas from "@/components/torq/GridCanvas";
import { scrollToId } from "@/components/torq/scrollTo";

const EASE = [0.16, 1, 0.3, 1];

function Line({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] flex flex-col overflow-hidden"
      data-testid="hero"
    >
      <motion.div style={{ y: gridY }} className="absolute inset-0 -bottom-24" data-testid="hero-grid">
        <GridCanvas className="w-full h-full" />
      </motion.div>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 100%, rgba(22,22,20,0.92) 0%, rgba(22,22,20,0.35) 45%, rgba(22,22,20,0.15) 100%)",
        }}
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex-1 flex flex-col justify-center lg:justify-between gap-8 lg:gap-10 px-6 lg:px-12 pt-24 lg:pt-36 pb-10 lg:pb-14"
      >
        <Line delay={0.15}>
          <p
            className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-[#ff5a1f]"
            data-testid="hero-eyebrow"
          >
            THE HYBRID FITNESS RACE — SEASON ONE
          </p>
        </Line>

        <h1 className="font-display font-black leading-[0.82] tracking-[0.005em]" data-testid="hero-title">
          <Line delay={0.25}>
            <span className="text-[clamp(2.75rem,13vw,15rem)] sm:text-[clamp(4.5rem,16vw,15rem)]">TORQ</span>
          </Line>
          <Line delay={0.38}>
            <span className="text-[clamp(2.75rem,13vw,15rem)] sm:text-[clamp(4.5rem,16vw,15rem)]">Circuit</span>
          </Line>
        </h1>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Line delay={0.55}>
            <div className="flex flex-wrap items-center gap-4">
              <span
                className="border border-[#f0eee4] font-mono text-[11px] tracking-[0.25em] px-4 py-2"
                data-testid="hero-badge-coming-soon"
              >
                COMING SOON
              </span>
              <span className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f]" data-testid="hero-location">
                DELHI, INDIA
              </span>
            </div>
          </Line>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4"
          >
            <a
              href="/register"
              data-testid="hero-cta-notify"
              className="btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.2em] px-8 py-4 min-h-[52px] w-full sm:w-auto inline-flex items-center justify-center hover:bg-white active:scale-[0.98] transition-transform"
            >
              REGISTER NOW
            </a>
            <a
              href="#format"
              onClick={scrollToId("format")}
              data-testid="hero-cta-format"
              className="btn-solid border border-[#454540] font-mono text-xs tracking-[0.2em] px-8 py-4 min-h-[52px] w-full sm:w-auto inline-flex items-center justify-center hover:border-[#f0eee4] active:scale-[0.98] transition-transform"
            >
              THE FORMAT
              <ArrowDown size={12} className="inline ml-2 -mt-0.5" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute right-6 lg:right-12 bottom-14 hidden md:flex flex-col items-center gap-3 z-10"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] [writing-mode:vertical-rl]">
          SCROLL
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-12 bg-[#454540]"
        />
      </motion.div>
    </section>
  );
}
